import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Backdrop,
  Button,
  ClickAwayListener,
  Grow,
  Paper,
  Portal,
  Popper,
  Typography,
  IconButton
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import moment from 'moment';
import { getEnvVar } from 'helpers';

const useStyles = makeStyles(theme => ({
  popperRoot: {
    zIndex: theme.zIndex.modal + 2
  },
  dateFilterContainer: {
    width: 500,
    maxWidth: '90vw'
  },
  dateFilterContent: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  filterSummaryText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  },
  dateFilterTop: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dateFilterMiddle: {
    padding: theme.spacing(2),
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dateFilterLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  dateFilterRight: {
    display: 'flex',
    flexDirection: 'column'
  },
  datePickerContainer: {
    width: '100%'
  },
  embeddedCalendar: {
    width: '100%',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    minHeight: 320,
    position: 'relative'
  },
  dailyRangeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  dailyRangeTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.9rem'
  },
  weekHeaderGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5)
  },
  weekHeaderCell: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    padding: theme.spacing(0.25, 0)
  },
  dailyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    gap: theme.spacing(0.5)
  },
  dailyCell: {
    height: 34,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '0.8rem',
    userSelect: 'none',
    transition: 'all 0.15s ease',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  dailyCellOutside: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover
  },
  dailyCellDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  dailyCellInRange: {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.action.selected
  },
  dailyCellStart: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16
  },
  dailyCellEnd: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16
  },
  dailyCellSingle: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '50%'
  },
  monthRangePanel: {
    width: '100%',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper
  },
  monthRangeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  monthRangeTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.9rem'
  },
  monthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1)
  },
  monthCell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1),
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover
    }
  },
  monthCellDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  monthCellInRange: {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.action.selected
  },
  monthCellStart: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  monthCellEnd: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  monthCellSingle: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  presetHelper: {
    padding: theme.spacing(1),
    textAlign: 'center',
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main
    },
    '&.selected': {
      backgroundColor: theme.palette.primary.light,
      borderColor: theme.palette.primary.main,
      fontWeight: 600
    }
  },
  selectionHint: {
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  dateFilterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2)
  },
  helperError: {
    color: theme.palette.error.main
  },
  backdropOverlay: {
    zIndex: 3500,
    backdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  calendarMonthYearTitle: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  monthYearPickerOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 1,
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column'
  },
  monthYearPickerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  monthYearPickerYear: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.9rem'
  },
  calendarMonthGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(1),
    flex: 1
  },
  calendarMonthCell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1),
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover
    }
  },
  calendarMonthCellDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  }
}));

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dailyRangePresets = [
  { value: 'TODAY', label: 'Today' },
  { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
  { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
  { value: 'LAST_60_DAYS', label: 'Last 60 Days' }
];
const monthlyRangePresets = [
  { value: 'LAST_MONTH', label: 'Last Month' },
  { value: 'LAST_2_MONTHS', label: 'Last 2 Months' },
  { value: 'LAST_12_MONTHS', label: 'Last 12 Months' },
  { value: 'LAST_24_MONTHS', label: 'Last 24 Months' }
];

const parseCronSchedule = (cronExpression, defaults) => {
  const parts = (cronExpression || '').trim().split(/\s+/);
  const toInt = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  return {
    minute: toInt(parts[1], defaults.minute),
    hour: toInt(parts[2], defaults.hour),
    dayOfMonth:
      parts[3] && parts[3] !== '?'
        ? toInt(parts[3], defaults.dayOfMonth)
        : defaults.dayOfMonth
  };
};

const DateFilterPopover = ({
  isActivityView = false,
  dateFilterDraft = { rangePreset: 'LAST_7_DAYS', fromDate: '', toDate: '' },
  rangeSelectionStep = 'start',
  visibleDailyMonth = moment().startOf('month'),
  visibleMonthYear = moment().year(),
  minAvailableDailyDate = moment().subtract(59, 'days'),
  maxAvailableDailyDate = moment(),
  minAvailableMonthlyDate = moment().subtract(23, 'months').startOf('month'),
  maxAvailableMonthlyDate = moment(),
  onDateFilterChange = () => {},
  onRangeSelectionStepChange = () => {},
  onVisibleDailyMonthChange = () => {},
  onVisibleMonthYearChange = () => {},
  onApplyDateFilters = () => {},
  onCloseDateFilter = () => {},
  dateFilterError = '',
  dateType = 'DAY',
  anchorEl = null,
  getPresetRange = () => {},
  queryData = {},
  dailyReportDelayHours,
  monthlyReportDelayHours,
  dailyRuntime,
  monthlyRuntime,
  hideDescription = false,
  popperStyle,
  showBackdrop = false,
  enableMonthYearPicker = false,
}) => {
  const classes = useStyles();
  const isDateFilterOpen = Boolean(anchorEl);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(() => visibleDailyMonth.year());

  React.useEffect(() => {
    if (!isDateFilterOpen) {
      setShowMonthYearPicker(false);
    }
  }, [isDateFilterOpen]);

  const configuredDailyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_DAILY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const configuredMonthlyReportDelayHours =
    parseInt(getEnvVar('ADM_REPORT_MONTHLY_REPORT_DELAY_HOURS') || '4', 10) || 4;
  const configuredDailyRuntime = parseCronSchedule(
    getEnvVar('ADM_REPORT_DAILY_AGGREGATION_RUNTIME') || '0 30 08 ? * *',
    { hour: 8, minute: 30, dayOfMonth: 1 }
  );
  const configuredMonthlyRuntime = parseCronSchedule(
    getEnvVar('ADM_REPORT_MONTHLY_REPORT_RUNTIME') || '0 30 14 2 * ?',
    { hour: 14, minute: 30, dayOfMonth: 2 }
  );

  const effectiveDailyReportDelayHours =
    Number.isFinite(dailyReportDelayHours) ? dailyReportDelayHours : configuredDailyReportDelayHours;
  const effectiveMonthlyReportDelayHours =
    Number.isFinite(monthlyReportDelayHours) ? monthlyReportDelayHours : configuredMonthlyReportDelayHours;
  const effectiveDailyRuntime = {
    ...configuredDailyRuntime,
    ...(dailyRuntime || {})
  };
  const effectiveMonthlyRuntime = {
    ...configuredMonthlyRuntime,
    ...(monthlyRuntime || {})
  };

  const formatLocalTimeFromUtc = (hour, minute, dayOfMonth = null) => {
    const localMoment = moment.utc()
      .startOf(dayOfMonth ? 'month' : 'day')
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0);

    if (dayOfMonth) {
      localMoment.date(dayOfMonth);
      return localMoment.local().format('D MMM, h:mm A');
    }

    return localMoment.local().format('h:mm A');
  };

  const dailyReadyMoment = useMemo(
    () => moment.utc().startOf('day').hour(effectiveDailyRuntime.hour).minute(effectiveDailyRuntime.minute).add(effectiveDailyReportDelayHours, 'hours'),
    [effectiveDailyRuntime.hour, effectiveDailyRuntime.minute, effectiveDailyReportDelayHours]
  );

  const monthlyReadyMoment = useMemo(
    () => moment.utc().startOf('month').date(effectiveMonthlyRuntime.dayOfMonth).hour(effectiveMonthlyRuntime.hour).minute(effectiveMonthlyRuntime.minute).add(effectiveMonthlyReportDelayHours, 'hours'),
    [effectiveMonthlyRuntime.dayOfMonth, effectiveMonthlyRuntime.hour, effectiveMonthlyRuntime.minute, effectiveMonthlyReportDelayHours]
  );

  const monthlyReadyDayLabel = useMemo(() => {
    const day = monthlyReadyMoment.clone().local().date();
    const mod10 = day % 10;
    const mod100 = day % 100;

    if (mod10 === 1 && mod100 !== 11) return `${day}st`;
    if (mod10 === 2 && mod100 !== 12) return `${day}nd`;
    if (mod10 === 3 && mod100 !== 13) return `${day}rd`;
    return `${day}th`;
  }, [monthlyReadyMoment]);

  const dailyInfoText = useMemo(() => {
    if (isActivityView) {
      return 'Live daily metrics are available up to the current date.';
    }
    const localUpdateMoment = dailyReadyMoment.clone().local();
    const updateTime = localUpdateMoment.format('h:mm A');
    const exampleBeforeTime = localUpdateMoment.clone().subtract(30, 'minutes').format('h:mm A');
    const exampleBeforeDate = moment().subtract(2, 'days').format('MMM D');
    const exampleAfterTime = updateTime;
    const yesterdayDate = moment().subtract(1, 'days').format('MMM D');
    
    return `Daily data is updated every day at ${updateTime}.\n\nBefore the update time: you will see data up to 2 days ago\n(e.g., at ${exampleBeforeTime} → data available up to ${exampleBeforeDate})\n\nAfter the update time: the data for yesterday becomes available\n(e.g., after ${exampleAfterTime} → ${yesterdayDate} data is visible)`;
  }, [
    isActivityView,
    dailyReadyMoment
  ]);

  const monthlyInfoText = useMemo(() => {
    const updateDay = effectiveMonthlyRuntime.dayOfMonth;
    const updateTime = monthlyReadyMoment.clone().local().format('h:mm A');
    const currentMonth = moment();
    const updateDaySuffix = updateDay === 1 ? 'st' : updateDay === 2 ? 'nd' : updateDay === 3 ? 'rd' : 'th';
    const beforeDay = updateDay > 1 ? updateDay - 1 : 1;
    const beforeExampleDate = currentMonth.clone().date(beforeDay).format('MMMM D');
    const afterExampleDate = currentMonth.clone().date(updateDay).format('MMMM D');
    const beforeAvailableMonth = currentMonth.clone().subtract(2, 'months').format('MMMM');
    const afterAvailableMonth = currentMonth.clone().subtract(1, 'months').format('MMMM');

    return `Monthly data is updated around the ${updateDay}${updateDaySuffix} of every month at ${updateTime}.\n\nBefore the ${updateDay}${updateDaySuffix}: you will see data only up to two months back\n(e.g., on ${beforeExampleDate} → data available up to ${beforeAvailableMonth})\n\nAfter the ${updateDay}${updateDaySuffix}: the previous month's data becomes available\n(e.g., on ${afterExampleDate} → ${afterAvailableMonth} data becomes available)`;
  }, [
    monthlyReadyDayLabel,
    monthlyReadyMoment,
    effectiveMonthlyRuntime.dayOfMonth
  ]);

  const weekdayLabels_memo = useMemo(() => weekdayLabels, []);
  
  const minAvailableDailyDate_memo = useMemo(
    () => minAvailableDailyDate.clone().startOf('day'),
    [minAvailableDailyDate]
  );
  const maxAvailableDailyDate_memo = useMemo(() => maxAvailableDailyDate.clone().startOf('day'), [maxAvailableDailyDate]);
  
  const dailyRangeBounds = useMemo(() => {
    const fromTs = dateFilterDraft.fromDate ? moment(dateFilterDraft.fromDate).startOf('day').valueOf() : null;
    const toTs = dateFilterDraft.toDate ? moment(dateFilterDraft.toDate).startOf('day').valueOf() : null;
    const hasStart = fromTs !== null;
    const hasEnd = toTs !== null;
    const isSingleDay = hasStart && hasEnd && fromTs === toTs;
    const minTs = hasStart && hasEnd ? Math.min(fromTs, toTs) : null;
    const maxTs = hasStart && hasEnd ? Math.max(fromTs, toTs) : null;
    return {
      hasStart,
      hasEnd,
      startTs: minTs,
      endTs: maxTs,
      isSingleDay
    };
  }, [dateFilterDraft.fromDate, dateFilterDraft.toDate]);

  const dailyGridDays = useMemo(() => {
    const startDate = visibleDailyMonth.clone().startOf('month').day(0);
    const days = [];
    for (let i = 0; i < 42; i++) {
      days.push(startDate.clone().add(i, 'days'));
    }
    return days;
  }, [visibleDailyMonth]);

  const isNextDailyMonthDisabled = useMemo(
    () => visibleDailyMonth.clone().add(1, 'month').startOf('month') > maxAvailableDailyDate_memo,
    [visibleDailyMonth, maxAvailableDailyDate_memo]
  );

  const isPrevDailyMonthDisabled = useMemo(
    () => visibleDailyMonth.clone().subtract(1, 'month').endOf('month') < minAvailableDailyDate_memo,
    [visibleDailyMonth, minAvailableDailyDate_memo]
  );

  const minAvailableMonth = useMemo(() => minAvailableMonthlyDate.clone().startOf('month'), [minAvailableMonthlyDate]);
  const maxAvailableMonth = useMemo(() => maxAvailableMonthlyDate.clone().endOf('month'), [maxAvailableMonthlyDate]);
  const minAvailableMonthId = useMemo(() => minAvailableMonth.year() * 12 + minAvailableMonth.month(), [minAvailableMonth]);
  const maxAvailableMonthId = useMemo(() => maxAvailableMonth.year() * 12 + maxAvailableMonth.month(), [maxAvailableMonth]);

  const monthlyRangeBounds = useMemo(() => {
    const getMonthId = (dateStr) => {
      const m = moment(dateStr);
      return m.year() * 12 + m.month();
    };
    const startMonthId = dateFilterDraft.fromDate ? getMonthId(dateFilterDraft.fromDate) : null;
    const endMonthId = dateFilterDraft.toDate ? getMonthId(dateFilterDraft.toDate) : null;
    const hasStart = startMonthId !== null;
    const hasEnd = endMonthId !== null;
    return {
      hasStart,
      hasEnd,
      startMonthId: hasStart ? Math.min(startMonthId, endMonthId) : null,
      endMonthId: hasEnd ? Math.max(startMonthId, endMonthId) : null,
      isSingleMonth: hasStart && hasEnd && startMonthId === endMonthId
    };
  }, [dateFilterDraft.fromDate, dateFilterDraft.toDate]);

  const isNextYearDisabled = visibleMonthYear >= maxAvailableMonth.year();
  const isPrevYearDisabled = visibleMonthYear <= minAvailableMonth.year();

  const availableDailyPresets = useMemo(() => {
    const nowLocalDay = moment().startOf('day');
    const maxDay = maxAvailableDailyDate_memo.clone().startOf('day');
    const isTodayAvailable = maxDay.isSame(nowLocalDay, 'day');

    return dailyRangePresets.filter(preset => {
      if (preset.value === 'TODAY') return isTodayAvailable;
      return true;
    });
  }, [maxAvailableDailyDate_memo]);

  const availableMonthlyPresets = useMemo(() => {
    const prevCalendarMonth = moment.utc().subtract(1, 'month').startOf('month');
    const isLastMonthAvailable = !prevCalendarMonth.isAfter(maxAvailableMonth, 'month');

    return monthlyRangePresets.filter(preset => {
      if (preset.value === 'LAST_MONTH') return isLastMonthAvailable;
      return true;
    });
  }, [maxAvailableMonth]);

  const safeFromDate = dateFilterDraft.fromDate || queryData?.fromDate || maxAvailableDailyDate_memo.format('YYYY-MM-DD');
  const safeToDate = dateFilterDraft.toDate || queryData?.toDate || maxAvailableDailyDate_memo.format('YYYY-MM-DD');

  const handleDailyCalendarChange = (day) => {
    if (rangeSelectionStep === 'start') {
      onDateFilterChange({
        ...dateFilterDraft,
        rangePreset: '',
        fromDate: day.format('YYYY-MM-DD'),
        toDate: day.format('YYYY-MM-DD')
      });
      onRangeSelectionStepChange('end');
    } else {
      const fromMoment = moment(dateFilterDraft.fromDate);
      const toMoment = day;
      const [minDate, maxDate] = fromMoment.isBefore(toMoment) 
        ? [fromMoment, toMoment] 
        : [toMoment, fromMoment];
      
      onDateFilterChange({
        ...dateFilterDraft,
        rangePreset: '',
        fromDate: minDate.format('YYYY-MM-DD'),
        toDate: maxDate.format('YYYY-MM-DD')
      });
      onRangeSelectionStepChange('start');
    }
  };

  const handleMonthlyRangeChange = (monthIndex) => {
    const selectedMonth = moment().year(visibleMonthYear).month(monthIndex).startOf('month');
    
    if (rangeSelectionStep === 'start') {
      onDateFilterChange({
        ...dateFilterDraft,
        rangePreset: '',
        fromDate: selectedMonth.format('YYYY-MM-DD'),
        toDate: selectedMonth.format('YYYY-MM-DD')
      });
      onRangeSelectionStepChange('end');
    } else {
      const fromMoment = moment(dateFilterDraft.fromDate).startOf('month');
      const toMoment = selectedMonth.clone();
      const [minDate, maxDate] = fromMoment.isBefore(toMoment) 
        ? [fromMoment, toMoment] 
        : [toMoment, fromMoment];
      
      onDateFilterChange({
        ...dateFilterDraft,
        rangePreset: '',
        fromDate: minDate.format('YYYY-MM-DD'),
        toDate: maxDate.endOf('month').format('YYYY-MM-DD')
      });
      onRangeSelectionStepChange('start');
    }
  };

  const handleRangePresetChange = preset => {
    const range = getPresetRange(preset, dateType);
    onRangeSelectionStepChange('start');
    if (dateType === 'DAY') {
      onVisibleDailyMonthChange(moment(range.toDate).startOf('month'));
    }
    if (dateType === 'MONTH') {
      onVisibleMonthYearChange(moment(range.toDate).year());
    }
    onDateFilterChange({
      ...dateFilterDraft,
      rangePreset: preset,
      fromDate: range.fromDate,
      toDate: range.toDate
    });
  };

  const isPresetSelected = preset => {
    if (dateFilterDraft.rangePreset !== preset) {
      return false;
    }

    const expectedRange = getPresetRange(preset, dateType);
    const selectedFrom = dateFilterDraft.fromDate ? moment(dateFilterDraft.fromDate).format('YYYY-MM-DD') : '';
    const selectedTo = dateFilterDraft.toDate ? moment(dateFilterDraft.toDate).format('YYYY-MM-DD') : '';

    return (
      expectedRange.fromDate === selectedFrom &&
      expectedRange.toDate === selectedTo
    );
  };

  return (
    <>
      {showBackdrop && (
        <Portal>
          <Backdrop open={isDateFilterOpen} className={classes.backdropOverlay} />
        </Portal>
      )}
      <Popper
      className={classes.popperRoot}
      style={popperStyle}
      open={isDateFilterOpen}
      anchorEl={anchorEl}
      placement="bottom-end"
      transition
      disablePortal={false}>
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} timeout={250}>
          <Paper className={classes.dateFilterContainer}>
            <ClickAwayListener onClickAway={onCloseDateFilter} mouseEvent="onMouseDown">
              <div className={classes.dateFilterContent}>
               {/* TOP SECTION: Description */}
{!hideDescription && (
  <div className={classes.dateFilterTop}>
    <Typography className={classes.filterSummaryText}>
      {dateType === 'MONTH'
        ? `Selected: ${moment(safeFromDate).format('MM-YYYY')} - ${moment(safeToDate).format('MM-YYYY')}`
        : `Selected: ${moment(safeFromDate).format('MM/DD/YYYY')} - ${moment(safeToDate).format('MM/DD/YYYY')}`}
    </Typography>
    <Typography className={classes.filterSummaryText} style={{ marginTop: 8, whiteSpace: 'pre-line' }}>
      {dateType === 'MONTH' ? monthlyInfoText : dailyInfoText}
    </Typography>
    <Typography className={classes.selectionHint}>
      {dateType === 'MONTH'
        ? (rangeSelectionStep === 'end'
          ? 'Select the ending month to complete the range, or choose a preset.'
          : 'Select a starting month and ending month, or choose a preset.')
        : (rangeSelectionStep === 'end'
          ? 'Select the ending date to complete the range, or choose a preset.'
          : 'Select a starting date and ending date, or choose a preset.')}
    </Typography>
  </div>
)}

                {/* MIDDLE SECTION: Left presets + Right selector */}
                <div className={classes.dateFilterMiddle} style={hideDescription ? { gridTemplateColumns: '1fr' } : {}}>
                  {!hideDescription && (
                    <div className={classes.dateFilterLeft}>
                      <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 8 }}>
                        Quick Presets
                      </Typography>
                      {(dateType === 'MONTH' ? availableMonthlyPresets : availableDailyPresets).map(preset => (
                        <div
                          key={preset.value}
                          className={`${classes.presetHelper} ${isPresetSelected(preset.value) ? 'selected' : ''}`}
                          onClick={() => handleRangePresetChange(preset.value)}>
                          {preset.label}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={classes.dateFilterRight}>
                    <div className={classes.datePickerContainer} onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                      {dateType === 'DAY' ? (
                        <div className={classes.embeddedCalendar}>
                      <div className={classes.dailyRangeHeader}>
                        <IconButton
                          size="small"
                          disabled={isPrevDailyMonthDisabled}
                          onClick={() => onVisibleDailyMonthChange(current => current.clone().subtract(1, 'month'))}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Typography
                          className={`${classes.dailyRangeTitle}${enableMonthYearPicker ? ` ${classes.calendarMonthYearTitle}` : ''}`}
                          onClick={enableMonthYearPicker ? () => {
                            setPickerYear(visibleDailyMonth.year());
                            setShowMonthYearPicker(prev => !prev);
                          } : undefined}>
                          {visibleDailyMonth.format('MMMM YYYY')}
                          {enableMonthYearPicker && <span style={{ fontSize: '0.7rem', marginLeft: 4 }}>&#9660;</span>}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={isNextDailyMonthDisabled}
                          onClick={() => onVisibleDailyMonthChange(current => current.clone().add(1, 'month'))}>
                          <ChevronRightIcon />
                        </IconButton>
                      </div>
                      <div className={classes.weekHeaderGrid}>
                        {weekdayLabels_memo.map(weekday => (
                          <div key={weekday} className={classes.weekHeaderCell}>{weekday}</div>
                        ))}
                      </div>
                      <div className={classes.dailyGrid}>
                        {dailyGridDays.map(day => {
                          const dayTs = day.clone().startOf('day').valueOf();
                          const isOutsideMonth = !day.isSame(visibleDailyMonth, 'month');
                          const isDisabled =
                            day.isAfter(maxAvailableDailyDate_memo, 'day') ||
                            day.isBefore(minAvailableDailyDate_memo, 'day');
                          const isStart = dailyRangeBounds.hasStart && dayTs === dailyRangeBounds.startTs;
                          const isEnd = dailyRangeBounds.hasEnd && dayTs === dailyRangeBounds.endTs;
                          const isInRange = dailyRangeBounds.hasStart && dailyRangeBounds.hasEnd && dayTs > dailyRangeBounds.startTs && dayTs < dailyRangeBounds.endTs;

                          let dayClassName = classes.dailyCell;
                          if (isOutsideMonth) dayClassName = `${dayClassName} ${classes.dailyCellOutside}`;
                          if (isDisabled) dayClassName = `${dayClassName} ${classes.dailyCellDisabled}`;
                          if (isInRange) dayClassName = `${dayClassName} ${classes.dailyCellInRange}`;
                          if (dailyRangeBounds.isSingleDay && isStart) {
                            dayClassName = `${dayClassName} ${classes.dailyCellSingle}`;
                          } else {
                            if (isStart) dayClassName = `${dayClassName} ${classes.dailyCellStart}`;
                            if (isEnd) dayClassName = `${dayClassName} ${classes.dailyCellEnd}`;
                          }

                          return (
                            <div
                              key={day.format('YYYY-MM-DD')}
                              className={dayClassName}
                              onClick={() => handleDailyCalendarChange(day)}>
                              {day.date()}
                            </div>
                          );
                        })}
                      </div>
                      {enableMonthYearPicker && showMonthYearPicker && (
                        <div className={classes.monthYearPickerOverlay}>
                          <div className={classes.monthYearPickerHeader}>
                            <IconButton
                              size="small"
                              disabled={pickerYear <= minAvailableDailyDate_memo.year()}
                              onClick={() => setPickerYear(y => y - 1)}>
                              <ChevronLeftIcon />
                            </IconButton>
                            <Typography className={classes.monthYearPickerYear}>{pickerYear}</Typography>
                            <IconButton
                              size="small"
                              disabled={pickerYear >= maxAvailableDailyDate_memo.year()}
                              onClick={() => setPickerYear(y => y + 1)}>
                              <ChevronRightIcon />
                            </IconButton>
                          </div>
                          <div className={classes.calendarMonthGrid}>
                            {monthLabels.map((label, idx) => {
                              const monthStart = moment().year(pickerYear).month(idx).startOf('month');
                              const monthEnd = moment().year(pickerYear).month(idx).endOf('month');
                              const isDisabled =
                                monthEnd.isBefore(minAvailableDailyDate_memo, 'day') ||
                                monthStart.isAfter(maxAvailableDailyDate_memo, 'day');
                              return (
                                <div
                                  key={label}
                                  className={`${classes.calendarMonthCell}${isDisabled ? ` ${classes.calendarMonthCellDisabled}` : ''}`}
                                  onClick={() => {
                                    if (!isDisabled) {
                                      onVisibleDailyMonthChange(monthStart.clone());
                                      setShowMonthYearPicker(false);
                                    }
                                  }}>
                                  {label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                        </div>
                      ) : (
                        <div className={classes.monthRangePanel}>
                      <div className={classes.monthRangeHeader}>
                        <IconButton
                          size="small"
                          disabled={isPrevYearDisabled}
                          onClick={() => onVisibleMonthYearChange(current => current - 1)}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Typography className={classes.monthRangeTitle}>
                          {visibleMonthYear}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={isNextYearDisabled}
                          onClick={() => onVisibleMonthYearChange(current => current + 1)}>
                          <ChevronRightIcon />
                        </IconButton>
                      </div>
                      <div className={classes.monthGrid}>
                        {monthLabels.map((label, monthIndex) => {
                          const monthId = (visibleMonthYear * 12) + monthIndex;
                          const isDisabled = monthId > maxAvailableMonthId || monthId < minAvailableMonthId;
                          const isStart = monthlyRangeBounds.hasStart && monthId === monthlyRangeBounds.startMonthId;
                          const isEnd = monthlyRangeBounds.hasEnd && monthId === monthlyRangeBounds.endMonthId;
                          const isInRange = monthlyRangeBounds.hasStart && monthlyRangeBounds.hasEnd && monthId > monthlyRangeBounds.startMonthId && monthId < monthlyRangeBounds.endMonthId;

                          let monthClassName = classes.monthCell;
                          if (isDisabled) monthClassName = `${monthClassName} ${classes.monthCellDisabled}`;
                          if (isInRange) monthClassName = `${monthClassName} ${classes.monthCellInRange}`;
                          if (monthlyRangeBounds.isSingleMonth && isStart) {
                            monthClassName = `${monthClassName} ${classes.monthCellSingle}`;
                          } else {
                            if (isStart) monthClassName = `${monthClassName} ${classes.monthCellStart}`;
                            if (isEnd) monthClassName = `${monthClassName} ${classes.monthCellEnd}`;
                          }

                          return (
                            <div
                              key={`${visibleMonthYear}-${label}`}
                              className={monthClassName}
                              onClick={() => handleMonthlyRangeChange(monthIndex)}>
                              {label}
                            </div>
                          );
                        })}
                      </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {dateFilterError && (
                  <Typography variant="body2" className={classes.helperError} style={{ marginTop: 8 }}>
                    {dateFilterError}
                  </Typography>
                )}

                {/* BOTTOM SECTION: Action Buttons */}
                <div className={classes.dateFilterActions}>
                  <Button onClick={onCloseDateFilter}>Cancel</Button>
                  <Button onClick={onApplyDateFilters} color="primary" variant="contained">
                    Apply
                  </Button>
                </div>
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
    </>
  );
};

export default DateFilterPopover;
